import { ReactNode } from 'react';
import IconCurrency from '@creditflowresearch/uilib/Icon/IconCurrency';
import IconCalendar from '@creditflowresearch/uilib/Icon/IconCalendar';
import IconPipeline from '@creditflowresearch/uilib/Icon/IconPipeline';
import { useRouter } from 'next/router';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import Product, { AbsProduct, ProductType } from '@/creditflowresearch/domain/Product';
import { UsoaRegion } from '@/creditflowresearch/domain/Region';
import { EmergingMarket, UsMarket, EuropeanMarket } from '@/creditflowresearch/domain/Market';
import URL from '@/creditflowresearch/Router/urlConstants';
import { FILTER_STATUS_ID_MAP } from '@framework/ui/Organisms/Filters/constants';
import { addQueryParams, removeQueryParams } from '@framework/router/nextRouter';
import { parseQueryParamValue } from '@/creditflowresearch/ui/common/utils/query/parseQueryParams';
import {
  ACTIVE_TAB_QUERY_PARAM,
  US_ACTIVE_CALENDAR_TAB,
} from '@/creditflowresearch/ui/components/ActiveCalendar/US/usActiveCalendarTabsConfig';

enum LinkKey {
  pricedDeals = 'pricedDeals',
  activeCalendar = 'activeCalendar',
  pipeline = 'pipeline',
  mandates = 'mandates',
}

interface ILinkConfig {
  url: string,
  statuses: string,
  tooltip: string,
  buildQuery: (query: IQueryParams) => Record<string, unknown>,
  shouldShow: (query: IQueryParams) => boolean,
}

interface ILinkData {
  key: LinkKey,
  icon: ReactNode,
  tooltip: string,
  pathname: string,
  query: Record<string, unknown>,
}

const LINK_ICONS: Record<LinkKey, ReactNode> = {
  [LinkKey.pricedDeals]: <IconCurrency />,
  [LinkKey.activeCalendar]: <IconCalendar />,
  [LinkKey.pipeline]: <IconPipeline />,
  [LinkKey.mandates]: <IconCalendar />,
};

export const ABS_ACTIVE_CALENDAR_STATUSES = [
  FILTER_STATUS_ID_MAP.mandate,
  FILTER_STATUS_ID_MAP.premarketing,
  FILTER_STATUS_ID_MAP.announced,
  FILTER_STATUS_ID_MAP.guidance,
  FILTER_STATUS_ID_MAP.launched,
].join(',');

export const CORPORATE_ACTIVE_CALENDAR_STATUSES = [
  FILTER_STATUS_ID_MAP.announced,
  FILTER_STATUS_ID_MAP.ipt,
  FILTER_STATUS_ID_MAP.guidance,
  FILTER_STATUS_ID_MAP.launched,
].join(',');

export const CORPORATE_PIPELINE_STATUSES = [
  FILTER_STATUS_ID_MAP.filed,
  FILTER_STATUS_ID_MAP.mandate,
].join(',');

export const ABS_STATUSES: Record<LinkKey, string> = {
  pricedDeals: `${ FILTER_STATUS_ID_MAP.priced }`,
  activeCalendar: ABS_ACTIVE_CALENDAR_STATUSES,
  pipeline: '',
  mandates: '',
};

export const CORPORATE_STATUSES: Record<LinkKey, string> = {
  pricedDeals: `${ FILTER_STATUS_ID_MAP.priced }`,
  activeCalendar: CORPORATE_ACTIVE_CALENDAR_STATUSES,
  pipeline: CORPORATE_PIPELINE_STATUSES,
  mandates: '',
};

const PARAMS_TO_REMOVE = [
  'articleType',
  'productType',
  'sector',
  'companyViewId',
  'issuerName',
  'sponsor',
  'sponsorName',
  'hash',
];

const cleanQuery = (query: IQueryParams, extra: string[] = []) =>
  removeQueryParams(query, [...PARAMS_TO_REMOVE, ...extra]);

const isIgcOnlySelected = (products: string | string[] | undefined): boolean => {
  const selectedProducts = parseQueryParamValue(products);

  return selectedProducts.length === 1 && Product.isOfType(selectedProducts[0], ProductType.IGC);
};

const COMBINED_MARKETS_KEY = [EuropeanMarket.ABBREVIATION, UsMarket.ABBREVIATION].join(',');
const FALLBACK_KEY = '__fallback__';

const LINK_CONFIGS: Record<string, Partial<Record<LinkKey, ILinkConfig>>> = {
  [AbsProduct.TYPE]: {
    [LinkKey.pricedDeals]: {
      url: URL.ABS_BONDSCREENER,
      statuses: ABS_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.activeCalendar]: {
      url: URL.ABS_ACTIVE_CALENDAR,
      statuses: '',
      tooltip: 'Active Calendar',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.pipeline]: {
      url: URL.PRODUCT_US_ABS_PIPELINE,
      statuses: ABS_STATUSES.pipeline,
      tooltip: '15G Pipeline',
      buildQuery: cleanQuery,
      shouldShow: (query) => query.regions === UsoaRegion.ABBREVIATION,
    },
  },
  [EmergingMarket.ABBREVIATION]: {
    [LinkKey.pricedDeals]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.activeCalendar]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.activeCalendar,
      tooltip: 'Active Calendar',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.pipeline]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pipeline,
      tooltip: 'Pipeline',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
  },
  [COMBINED_MARKETS_KEY]: {
    [LinkKey.pricedDeals]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
  },
  [UsMarket.ABBREVIATION]: {
    [LinkKey.pricedDeals]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.activeCalendar]: {
      url: URL.CORPORATE_ACTIVE_CALENDAR,
      statuses: '',
      tooltip: 'Active Calendar',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.mandates]: {
      url: URL.CORPORATE_ACTIVE_CALENDAR,
      statuses: CORPORATE_STATUSES.mandates,
      tooltip: 'Mandates',
      buildQuery: (query) => addQueryParams(cleanQuery(query), {
        [ACTIVE_TAB_QUERY_PARAM]: US_ACTIVE_CALENDAR_TAB.MANDATE,
      }),
      shouldShow: (query) => isIgcOnlySelected(query.products),
    },
  },
  [EuropeanMarket.ABBREVIATION]: {
    [LinkKey.pricedDeals]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.pipeline]: {
      url: URL.EUROPE_ACTIVE_CALENDAR,
      statuses: '',
      tooltip: 'European Pipeline',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
  },
  [FALLBACK_KEY]: {
    [LinkKey.pricedDeals]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pricedDeals,
      tooltip: 'Priced Deals',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.activeCalendar]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.activeCalendar,
      tooltip: 'Active Calendar',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
    [LinkKey.pipeline]: {
      url: URL.CORPORATE_BONDSCREENER,
      statuses: CORPORATE_STATUSES.pipeline,
      tooltip: 'Pipeline',
      buildQuery: cleanQuery,
      shouldShow: () => true,
    },
  },
};

const normalizeConfigKey = (query: IQueryParams): string => {
  if (Product.isOfType(query.products, AbsProduct.TYPE)) return AbsProduct.TYPE;

  const markets = Array.isArray(query.markets)
    ? query.markets?.sort().join(',')
    : (query.markets as string)?.split(',').sort().join(',');

  return (markets && LINK_CONFIGS[markets]) ? markets : FALLBACK_KEY;
};

export const useGetPricedDealsLink = (): ILinkData[] => {
  const { query }: { query: IQueryParams } = useRouter();
  const configs = LINK_CONFIGS[normalizeConfigKey(query)];

  return (Object.keys(configs) as LinkKey[])
    .filter((linkKey) => configs[linkKey].shouldShow(query))
    .map((linkKey) => {
      const config = configs[linkKey];
      const builtQuery = config.buildQuery(query);

      return {
        key: linkKey,
        icon: LINK_ICONS[linkKey],
        tooltip: config.tooltip,
        pathname: config.url,
        query: config.statuses
          ? addQueryParams(builtQuery, { statuses: config.statuses })
          : builtQuery,
      };
    });
};
